// [S-802 follow-up] A variable as a CKEditor MODEL MARKER, not as markup in the
// document.
//
// `variableHighlight.ts` wraps each slot in `<span class="tv-mark">` and strips
// it again on save. That span is ordinary content: typing over it deletes it,
// the editor may split or merge it, and — the expensive part — the slot's
// recorded offsets never move, so ANY edit anywhere makes the whole tag set
// stale (`sourceHash` mismatch → approve and fill refuse, drafting silently
// falls back to the generator).
//
// A marker is a named range the editor keeps OUTSIDE the data and transforms
// through every edit. That is the same idea the tag set already has ("a
// variable is not in the document"), except the editor maintains it. So an
// author can type above, beside, or inside a variable and it still covers the
// right characters, and on save the offsets can be RE-ANCHORED instead of
// invalidated.
//
// This module is the seam between the two worlds, and it deliberately does NOT
// walk CKEditor's model to compute offsets. A model walker would be a second
// implementation of `templateTextForTagging` that can disagree with the first
// (rule 15). Instead, markers enter and leave the editor as BOUNDARY ELEMENTS
// in the HTML — which is exactly the shape CKEditor's own `dataToMarker` /
// `markerToData` conversion speaks — and the existing, corpus-tested offset
// mapping does the rest, in both directions.
import { flattenedToHtmlOffsets, htmlSpanOf, templateTextForTagging } from "@csai/core/browser";

/** A slot, as the variables API returns it. Same shape variableHighlight uses. */
export type MarkerSlot = { id: string; start: number; end: number; sameAs?: string | null };

/** The conversion group. CKEditor names the marker `<GROUP>:<name>`, and the
 *  boundary elements `<GROUP-start name="…">` / `<GROUP-end name="…">`. */
export const MARKER_GROUP = "tv";

const START = new RegExp(`<${MARKER_GROUP}-start\\b[^>]*>(?:</${MARKER_GROUP}-start>)?`, "gi");
const ANY_BOUNDARY = new RegExp(
  `<\\/?${MARKER_GROUP}-(?:start|end)\\b[^>]*>`,
  "gi"
);
const NAME_ATTR = /\bname="([^"]*)"/i;

export type PlacementResult = {
  /** `html` with a boundary pair around each placeable slot. */
  html: string;
  /** Slot ids that became boundaries. */
  placed: string[];
  /** Slot ids the offset map could not vouch for — drawn nowhere, as before. */
  skipped: string[];
};

/**
 * Wrap each slot in boundary elements, ready for the editor to upcast into
 * markers.
 *
 * Unlike `withVariableMarks`, a slot whose span contains markup is NOT skipped:
 * the two boundaries are independent elements, so a variable that straddles a
 * `<strong>` needs no wrapping tag and cannot produce the mis-nested document
 * that made the span approach refuse. Those slots are invisible in the editor
 * today, which makes them the ones an author is most likely to destroy without
 * noticing.
 */
export function withMarkerBoundaries(
  html: string,
  slots: readonly MarkerSlot[]
): PlacementResult {
  if (slots.length === 0) return { html, placed: [], skipped: [] };
  const map = flattenedToHtmlOffsets(html, templateTextForTagging(html));
  const placed: string[] = [];
  const skipped: string[] = [];
  // One insertion per boundary, applied back-to-front so earlier offsets stay
  // valid. `at` ties are ordered so a slot's own end precedes the next slot's
  // start, and a zero-width slot still nests start-before-end.
  const inserts: Array<{ at: number; order: number; text: string }> = [];
  for (const slot of slots) {
    const span = htmlSpanOf(map, slot.start, slot.end);
    if (span === null) {
      skipped.push(slot.id);
      continue;
    }
    const name = escapeAttr(slot.id);
    inserts.push({ at: span.from, order: 1, text: `<${MARKER_GROUP}-start name="${name}"></${MARKER_GROUP}-start>` });
    inserts.push({ at: span.to, order: 0, text: `<${MARKER_GROUP}-end name="${name}"></${MARKER_GROUP}-end>` });
    placed.push(slot.id);
  }
  let out = html;
  const ordered = [...inserts].sort((a, b) => (b.at - a.at) || (b.order - a.order));
  for (const ins of ordered) out = out.slice(0, ins.at) + ins.text + out.slice(ins.at);
  return { html: out, placed, skipped };
}

/** Everything the boundaries added, removed — what gets stored. */
export function stripMarkerBoundaries(html: string): string {
  ANY_BOUNDARY.lastIndex = 0;
  return html.replace(ANY_BOUNDARY, "");
}

/** True when this HTML carries boundaries (so a caller can avoid re-placing). */
export function hasMarkerBoundaries(html: string): boolean {
  ANY_BOUNDARY.lastIndex = 0;
  return ANY_BOUNDARY.test(html);
}

export type ReadSlot = {
  id: string;
  start: number;
  end: number;
  /** True when the author deleted everything the variable covered. The set
   *  should report this one variable by name — not invalidate the other forty. */
  collapsed: boolean;
};

export type ReadResult = {
  /** The document as it should be stored: boundaries removed. */
  html: string;
  /** Each variable's offsets into `templateTextForTagging(html)`, re-anchored
   *  to where the author left it. */
  slots: ReadSlot[];
  /** Names that lost a boundary (one half deleted) — re-anchoring cannot speak
   *  for these, so a caller keeps the old offsets and flags them. */
  unpaired: string[];
};

/**
 * The inverse: read boundaries back out of what the editor produced and turn
 * them into offsets against the stored (boundary-free) document.
 *
 * The offsets are computed the same way they were first recorded — through
 * `templateTextForTagging` + `flattenedToHtmlOffsets` on the stored HTML — so
 * a re-anchored set is indistinguishable in kind from a freshly proposed one.
 */
export function slotsFromMarkerBoundaries(markedHtml: string): ReadResult {
  const positions = boundaryPositionsInStripped(markedHtml);
  const html = stripMarkerBoundaries(markedHtml);
  const flat = templateTextForTagging(html);
  const map = flattenedToHtmlOffsets(html, flat);
  const htmlToFlat = invertOffsetMap(map, html.length);

  const slots: ReadSlot[] = [];
  const unpaired: string[] = [];
  for (const [name, pos] of positions) {
    if (pos.start === undefined || pos.end === undefined) {
      unpaired.push(name);
      continue;
    }
    // A start boundary names the character it PRECEDES; an end boundary names
    // the character it FOLLOWS. Reading both forward puts the end one character
    // late whenever a slot ends at a block boundary, where the next flattened
    // character is the newline `</p>` produced — measured as 640 of 1360 slots.
    const start = flatIndexAtOrAfter(htmlToFlat, pos.start, flat.length);
    const end = flatIndexAfterPrevious(htmlToFlat, pos.end);
    slots.push({ id: name, start, end: Math.max(start, end), collapsed: end <= start });
  }
  slots.sort((a, b) => a.start - b.start || a.end - b.end);
  return { html, slots, unpaired };
}

/** Each boundary's index in the html AFTER every boundary is removed — the
 *  coordinate system the stored document uses. */
function boundaryPositionsInStripped(
  markedHtml: string
): Map<string, { start?: number; end?: number }> {
  const out = new Map<string, { start?: number; end?: number }>();
  ANY_BOUNDARY.lastIndex = 0;
  let removed = 0;
  for (let m = ANY_BOUNDARY.exec(markedHtml); m !== null; m = ANY_BOUNDARY.exec(markedHtml)) {
    const tag = m[0];
    if (tag.startsWith("</")) {
      removed += tag.length;
      continue;
    }
    const name = decodeAttr(NAME_ATTR.exec(tag)?.[1] ?? "");
    if (name.length === 0) {
      removed += tag.length;
      continue;
    }
    const entry = out.get(name) ?? {};
    const where = m.index - removed;
    START.lastIndex = 0;
    if (START.test(tag)) entry.start = entry.start ?? where;
    else entry.end = where;
    out.set(name, entry);
    removed += tag.length;
  }
  return out;
}

/** html index → flattened index, for every html index a flattened char came
 *  from. Holes stay -1; the reader walks forward past them. */
function invertOffsetMap(map: readonly number[], htmlLength: number): Int32Array {
  const inv = new Int32Array(htmlLength + 1).fill(-1);
  for (let f = map.length - 1; f >= 0; f -= 1) {
    const h = map[f]!;
    if (h >= 0 && h <= htmlLength) inv[h] = f;
  }
  return inv;
}

/** The first flattened index at or after this html index — where a START
 *  boundary sits, naming the character it precedes. */
function flatIndexAtOrAfter(inv: Int32Array, htmlIndex: number, flatLength: number): number {
  for (let h = Math.max(0, htmlIndex); h < inv.length; h += 1) {
    const f = inv[h]!;
    if (f >= 0) return f;
  }
  return flatLength;
}

/** One past the last flattened index BEFORE this html index — where an END
 *  boundary sits, naming the character it follows. */
function flatIndexAfterPrevious(inv: Int32Array, htmlIndex: number): number {
  for (let h = Math.min(htmlIndex, inv.length) - 1; h >= 0; h -= 1) {
    const f = inv[h]!;
    if (f >= 0) return f + 1;
  }
  return 0;
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
}

function decodeAttr(s: string): string {
  return s.replace(/&lt;/g, "<").replace(/&quot;/g, '"').replace(/&amp;/g, "&");
}
